'use client';
import { useEffect, useRef, useState } from 'react';
import NavBar from './NavBar';
import HeroSection from './HeroSection';
import TechStackSection from './TechStackSection';
import AboutSection from './AboutSection';
import ServicesSection from './ServicesSection';
import PortfolioSection from './PortfolioSection';
import ClientsSection from './ClientsSection';
import ContactSection from './ContactSection';
import FooterSection from './FooterSection';

export default function HomepageInteractive() {
  const [isDark, setIsDark] = useState(true);
  const [mounted, setMounted] = useState(false);
  const cursorRef = useRef<HTMLDivElement>(null);
  const followerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Theme toggle
  const toggleTheme = () => {
    setIsDark((prev) => {
      const next = !prev;
      if (next) {
        document.documentElement.classList.remove('light');
      } else {
        document.documentElement.classList.add('light');
      }
      return next;
    });
  };

  // Custom cursor
  useEffect(() => {
    const cursor = cursorRef.current;
    const follower = followerRef.current;
    if (!cursor || !follower) return;

    let mouseX = 0,
      mouseY = 0;
    let followerX = 0,
      followerY = 0;

    const onMouseMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      cursor.style.left = `${mouseX}px`;
      cursor.style.top = `${mouseY}px`;
    };

    const animate = () => {
      followerX += (mouseX - followerX) * 0.12;
      followerY += (mouseY - followerY) * 0.12;
      follower.style.left = `${followerX}px`;
      follower.style.top = `${followerY}px`;
      requestAnimationFrame(animate);
    };

    window.addEventListener('mousemove', onMouseMove);
    const raf = requestAnimationFrame(animate);

    // Scale on hover
    const onEnter = () => {
      cursor.style.width = '20px';
      cursor.style.height = '20px';
      follower.style.width = '60px';
      follower.style.height = '60px';
    };
    const onLeave = () => {
      cursor.style.width = '12px';
      cursor.style.height = '12px';
      follower.style.width = '36px';
      follower.style.height = '36px';
    };

    document.querySelectorAll('a, button').forEach((el) => {
      el.addEventListener('mouseenter', onEnter);
      el.addEventListener('mouseleave', onLeave);
    });

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            entry.target.classList.remove('initially-hidden');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );

    const elements = document.querySelectorAll('.initially-hidden');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div
      suppressHydrationWarning
      className={mounted && !isDark ? 'light' : ''}
      style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}
    >
      {/* Noise overlay */}
      <div className="noise-overlay" />

      {/* Custom cursor (hidden on touch devices) */}
      <div ref={cursorRef} className="custom-cursor hidden lg:block" />
      <div ref={followerRef} className="cursor-follower hidden lg:block" />

      {/* Navigation */}
      <NavBar isDark={isDark} toggleTheme={toggleTheme} />

      {/* Main content */}
      <main>
        <HeroSection />
        <TechStackSection />
        <AboutSection />
        <ServicesSection />
        <PortfolioSection />
        <ClientsSection />
        <ContactSection />
      </main>

      <FooterSection />
    </div>
  );
}
